/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Audience snapshots", description = "Write audience membership history to your warehouse, including when members enter or exit an audience.", lastUpdatedDate = new Date(1787788800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Audience snapshots",
        description: "Write audience membership history to your warehouse, including when members enter or exit an audience.",
        lastUpdatedDate: "Thu Aug 27 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How snapshots work",
          "depth": 0,
          "id": "how-snapshots-work"
        }, {
          "value": "Snapshot table schema",
          "depth": 0,
          "id": "snapshot-table-schema"
        }, {
          "value": "Enable snapshots",
          "depth": 0,
          "id": "enable-snapshots"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      h2: "h2",
      ul: "ul",
      li: "li",
      code: "code",
      ol: "ol",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins who want a historical record of audience membership"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A source with the ", _jsx(_components.a, {
                    href: "/syncs/lightning-sync-engine",
                    children: "Lightning sync engine"
                  }), " enabled"]
                }), _jsxs("li", {
                  children: ["Warehouse ", _jsx(_components.a, {
                    href: "/getting-started/concepts#sources",
                    children: "source"
                  }), " with ", _jsx("strong", {
                    children: "write"
                  }), " permissions"]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Audience snapshots record who was in an audience and when members entered or exited. Hightouch writes this history to your warehouse as a table you can query."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Audience ", _jsx(_components.a, {
          href: "/customer-studio/audience-overviews",
          children: "dashboards"
        }), " show membership trends inside Hightouch. Snapshots store audience membership changes in your warehouse, where you can query them directly or join them with other data for reporting and analysis."]
      }), "\n", _jsx(_components.h2, {
        id: "how-snapshots-work",
        children: "How snapshots work"
      }), "\n", _jsx(_components.p, {
        children: "After each successful audience sync, Customer Studio writes membership changes to the snapshot table. Each record includes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience member ID"
          }), " — The primary key of the audience member."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Timestamp"
          }), " — When the member entered or exited the audience."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Event type"
          }), " — Either ", _jsx(_components.code, {
            children: "enter"
          }), " or ", _jsx(_components.code, {
            children: "exit"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Split group ID"
          }), " — The member's split group when audience ", _jsx(_components.a, {
            href: "/customer-studio/experiments",
            children: "experiments"
          }), " are enabled."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The first snapshot records every current audience member as an ", _jsx(_components.code, {
          children: "enter"
        }), " event. Later snapshots record only changes since the previous snapshot:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["an ", _jsx(_components.code, {
            children: "enter"
          }), " event when a member joins the audience"]
        }), "\n", _jsxs(_components.li, {
          children: ["an ", _jsx(_components.code, {
            children: "exit"
          }), " event when a member leaves the audience"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "snapshot-table-schema",
        children: "Snapshot table schema"
      }), "\n", _jsxs(_components.p, {
        children: ["Snapshots are stored in a shared table in the ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine#warehouse-schemas",
          children: "Hightouch Planner schema"
        }), ": ", _jsx(_components.code, {
          children: "hightouch_planner.audience_membership"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ht_row_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "string"
              })
            }), _jsx(_components.td, {
              children: "The primary key of the audience member. Hightouch converts the value to a string when needed."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ht_audience_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "string"
              })
            }), _jsx(_components.td, {
              children: "The ID of the audience."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ht_timestamp"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "When the member entered or exited the audience."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ht_event_type"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "string"
              })
            }), _jsxs(_components.td, {
              children: ["Either ", _jsx(_components.code, {
                children: "enter"
              }), " or ", _jsx(_components.code, {
                children: "exit"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ht_split_group"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "string"
              })
            }), _jsxs(_components.td, {
              children: ["The split group ID the member belongs to, or ", _jsx(_components.code, {
                children: "null"
              }), " if no split was applied."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "enable-snapshots",
        children: "Enable snapshots"
      }), "\n", _jsxs(_components.p, {
        children: ["Audience snapshots are configured as part of ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "warehouse sync logs"
        }), " and enabled per source:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to your ", _jsx(_components.a, {
            href: "/getting-started/concepts#sources",
            children: "source"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Sync logs"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Warehouse sync logs"
          }), ", enable ", _jsx(_components.strong, {
            children: "Audience snapshots"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch records membership changes automatically after each successful audience sync."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/performance/enable-snapshot.png",
          alt: "Enable audience snapshots"
        })
      })]
    });
  }
}
export default MDXContent;
